import { FC, useEffect } from 'react';
import { Box, VStack } from '@chakra-ui/react';

import Callout from 'components/_core/Callout';
import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import BackButton from 'components/BackButton';
import PasscodeInput from 'components/PasscodeInput';
import StepperProgressBar from 'components/Stepper/StepperProgressBar';
import { StepProps } from 'components/Stepper/types';
import * as Layout from 'layouts/StepperLayout';

import { CtaButton } from '../../../components/Buttons';
import { usePhysicalCardOrderFlowContext } from '../../hooks/usePhysicalCardOrderFlowContext';
import ToggleShowPasscode from './components/ToggleShowPasscode';
import locales from './locales';

const PhysicalCardOrderCardPin: FC<StepProps> = (props) => {
  const { goBack, goNext } = props;

  const { setSharedState, sharedState } = usePhysicalCardOrderFlowContext();
  const savePinInSharedState = (pin: string) => {
    setSharedState({ ...sharedState, pin });
  };

  useEffect(() => {
    savePinInSharedState('');
    // We only want to clear the PIN code on the first render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onBackPressed = () => {
    // No need to show an error if the user goes back and then forth
    setSharedState({ ...sharedState, pinConfirmationWasDifferent: false });
    goBack();
  };

  const handleNext = (pin: string) => {
    savePinInSharedState(pin);
    goNext();
  };

  // The "choose PIN" step is composed of two different steps: the user first chooses
  // a PIN and then confirms it in a second step.
  // In the case where the confirmation PIN is different from the initial one, the
  // user is redirected to the first PIN step (the current component).
  // The value `pinConfirmationWasDifferent` in the sharedState is used to know
  // if such an error ocurred and display a contextual error message.
  const { isPasscodeShown, pinConfirmationWasDifferent } = sharedState;

  return (
    <Layout.Root>
      <Layout.Body>
        <Layout.HeaderButtonsContainer>
          <BackButton onClick={onBackPressed} />
        </Layout.HeaderButtonsContainer>

        <Layout.BodyScrollableSection>
          <VStack alignItems="stretch" spacing="space-8">
            <Heading size="lg">{locales.title}</Heading>
            <Text size="lg">{locales.subtitle}</Text>
          </VStack>

          <Box marginTop="space-32">
            <PasscodeInput hidden={!isPasscodeShown} onSubmit={handleNext} />
          </Box>

          <Box marginTop="space-32">
            <ToggleShowPasscode />
          </Box>

          {pinConfirmationWasDifferent ? (
            <Callout marginTop="space-32" variant="error">
              <Callout.Content>
                {locales.pinConfirmationMismatched}
              </Callout.Content>
            </Callout>
          ) : null}
        </Layout.BodyScrollableSection>

        <Layout.FooterContainer>
          <StepperProgressBar />
          <Layout.FooterButtonsContainer>
            <CtaButton isDisabled>{locales.next}</CtaButton>
          </Layout.FooterButtonsContainer>
        </Layout.FooterContainer>
      </Layout.Body>
    </Layout.Root>
  );
};

export default PhysicalCardOrderCardPin;
